import React, { useState, useEffect, useRef } from "react";
import DropdownMenuAndComment from "../DropdownMenuAndComment/DropdownMenuAndComment";

interface CmrtV2ComplianceAnswerModel {
    Answer: string;
    Comment: string;
}

interface CmrtV2ComplianceMultiAnswerModel {
    [key: string]: CmrtV2ComplianceAnswerModel;
}

interface CMRTMineralsComponentProps<T> {
    localizer: any;
    options: T[];
    value: string;
    getFriendlyNameForOptions: (option: T) => string;
    getUniqueValueForOption: (option: T) => string;
    isDisabled?: boolean;
    labels: string[];
    onValueChange: (value: string) => void;
}

const Metals: (keyof CmrtV2ComplianceMultiAnswerModel)[] = ["Ta", "Sn", "Au", "W"];

const CMRTMineralsComponent = <T extends string>({
    localizer,
    options,
    value,
    getFriendlyNameForOptions,
    getUniqueValueForOption,
    isDisabled,
    labels,
    onValueChange,
}: CMRTMineralsComponentProps<T>) => {
    const defaultAnswer = options[0] === "Choose One" ? "" : options[0];
    const isFirstRender = useRef(true);

    const parseValue = (input: string): CmrtV2ComplianceMultiAnswerModel => {
        try {
            const parsedValues = JSON.parse(input ?? "{}");
            return Metals.reduce((acc, key) => {
                acc[key] = parsedValues[key] || { Answer: defaultAnswer, Comment: "" };
                return acc;
            }, {} as CmrtV2ComplianceMultiAnswerModel);
        } catch {
            return Metals.reduce((acc, key) => {
                acc[key] = { Answer: defaultAnswer, Comment: "" };
                return acc;
            }, {} as CmrtV2ComplianceMultiAnswerModel);
        }
    };

    const [cmrtMineralResponses, setCmrtMineralResponses] = useState<CmrtV2ComplianceMultiAnswerModel>(parseValue(value));

    useEffect(() => {
        setCmrtMineralResponses(parseValue(value));
    }, [value]);

    const handleValueChange = (key: keyof CmrtV2ComplianceMultiAnswerModel) => (updatedValue: CmrtV2ComplianceAnswerModel) => {
        // Convert "Choose One" to empty string if needed
        if (updatedValue.Answer === "Choose One") {
            updatedValue.Answer = "";
        }
        setCmrtMineralResponses(prevValues => {
            const newResponses = { ...prevValues, [key]: updatedValue };
            // Call onValueChange immediately after calculating the new state
            // This ensures the parent gets the update once per interaction originating from DropdownMenuAndComment
            onValueChange(JSON.stringify(newResponses));
            return newResponses;
        });
    };

    return (
        <div>
            {Metals.map((key, index) => (
                <div key={key} className="dropdown-comment-pair">
                    <DropdownMenuAndComment
                        localizer={localizer}
                        options={options}
                        value={JSON.stringify(cmrtMineralResponses[key])}
                        onValueChange={(updatedJson) => {
                            const updatedValue = JSON.parse(updatedJson);
                            handleValueChange(key)(updatedValue);
                        }}
                        getFriendlyNameForOptions={getFriendlyNameForOptions}
                        getUniqueValueForOption={getUniqueValueForOption}
                        isDisabled={isDisabled}
                        label={labels[index] ?? "Answer"}
                    />
                </div>
            ))}
        </div>
    );
};

export default CMRTMineralsComponent;
